import { createContext, useCallback, useContext, useEffect, useMemo, useState, type FC, type PropsWithChildren } from 'react'

interface BannerSlotState {
  id: string
  priority: number
  isFetching: boolean
  shouldShow: boolean
}

interface BannerStackContextValue {
  registerSlot: (id: string, state: BannerSlotState) => void
  unregisterSlot: (id: string) => void
  activeSlotId: string | null
}

const BannerStackContext = createContext<BannerStackContextValue | null>(null)

export const BannerStack: FC<PropsWithChildren> = ({ children }) => {
  const [slots, setSlots] = useState<Record<string, BannerSlotState>>({})

  const registerSlot = useCallback((id: string, state: BannerSlotState) => {
    setSlots((current) => {
      const existing = current[id]
      if (
        existing !== undefined &&
        existing.priority === state.priority &&
        existing.isFetching === state.isFetching &&
        existing.shouldShow === state.shouldShow
      ) {
        return current
      }
      return { ...current, [id]: state }
    })
  }, [])

  const unregisterSlot = useCallback((id: string) => {
    setSlots((current) => {
      if (current[id] === undefined) {
        return current
      }
      return Object.fromEntries(Object.entries(current).filter(([key]) => key !== id))
    })
  }, [])

  const activeSlotId = useMemo(() => {
    const first = Object.values(slots)
      .sort((a, b) => a.priority - b.priority)
      .find((slot) => slot.isFetching || slot.shouldShow)
    return first !== undefined && !first.isFetching ? first.id : null
  }, [slots])

  const value = useMemo<BannerStackContextValue>(() => ({ registerSlot, unregisterSlot, activeSlotId }), [registerSlot, unregisterSlot, activeSlotId])

  return <BannerStackContext.Provider value={value}>{children}</BannerStackContext.Provider>
}

interface BannerSlotProps extends PropsWithChildren {
  id: string
  priority: number
  isFetching: boolean
  shouldShow: boolean
}

export const BannerSlot: FC<BannerSlotProps> = ({ id, priority, isFetching, shouldShow, children }) => {
  const stack = useContext(BannerStackContext)
  const registerSlot = stack?.registerSlot
  const unregisterSlot = stack?.unregisterSlot

  useEffect(() => {
    if (registerSlot === undefined) {
      return
    }
    registerSlot(id, { id, priority, isFetching, shouldShow })
  }, [id, priority, isFetching, shouldShow, registerSlot])

  // Deliberately a second effect: folding this cleanup into the effect above would unregister and
  // re-register the slot on every prop change, flickering `activeSlotId` to null in between. A child
  // cannot set parent state during render, so both effects are required here.
  useEffect(() => {
    return () => {
      unregisterSlot?.(id)
    }
  }, [id, unregisterSlot])

  if (stack === null) {
    return shouldShow && !isFetching ? <>{children}</> : null
  }

  return stack.activeSlotId === id ? <>{children}</> : null
}
